class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

    // Bind immediately for initial markup.
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.openFromRedirectIfNeeded();
    // Some headers (especially overlay/mobile variants) can be re-rendered or replaced after
    // `cart-drawer.js` runs. Observe DOM mutations and (re)bind any new cart icons.
    if (this.headerCartIconObserver) return;

    this.headerCartIconObserver = new MutationObserver(() => {
      this.setHeaderCartIconAccessibility();
    });

    this.headerCartIconObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.headerCartIconObserver) {
      this.headerCartIconObserver.disconnect();
      this.headerCartIconObserver = null;
    }
  }
  openFromRedirectIfNeeded() {
    let shouldOpen = false;
    try {
      shouldOpen = sessionStorage.getItem('kk_open_cart_drawer_on_load') === '1';
      if (shouldOpen) sessionStorage.removeItem('kk_open_cart_drawer_on_load');
    } catch (error) {
      shouldOpen = false;
    }

    if (!shouldOpen) return;
    requestAnimationFrame(() => this.open());
  }

  setHeaderCartIconAccessibility() {
    // In addition to Dawn's default #cart-icon-bubble, this theme also renders cart icons
    // in overlay headers / floating icon containers.
    const cartUrl = window?.routes?.cart_url;
    const selectors = [
      '#cart-icon-bubble',
      '[id^="cart-icon-bubble-"]',

      // Explicit overlay-header hooks (more stable than classnames if markup changes).
      'a[data-mobile-header-cart]',
      'a[data-mobile-floating-cart]',

      // Class-based fallbacks.
      'a.overlay-header-cart-icon',
      'a.main-overlay-header-cart-icon',
      ...(cartUrl ? [`a[href="${cartUrl}"]`] : []),
    ];

    const cartLinks = Array.from(new Set(document.querySelectorAll(selectors.join(','))));
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      // Avoid double-binding if sections re-render.
      if (cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        // Preserve standard “open in new tab/window” behaviors.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Ensure the browser paints the hidden state before activating,
    // so the slide/fade transition always plays.
    this.classList.add('animate');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
